import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';

import useBodyScrollLock from '../../../modules/common/hooks/useBodyScrollLock';
import { zIndex } from '../../../modules/common/constants';
import { Icon } from '../..';
import { breakpoints, colors } from '../../systemprovider';
import { ProductImageListMobile } from 'src/shared/modules/product/utils';

const StyledZoomImageContainer = styled.div`
  .zoom-image-close-btn,
  .zoom-image-left-btn,
  .zoom-image-right-btn {
    position: fixed;
    z-index: ${zIndex.MODAL + 1};
    height: 32px;
    width: 32px;
    border: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colors.WHITE};

    &::before {
      content: '';
      position: absolute;
      top: -6px;
      bottom: -6px;
      left: -6px;
      right: -6px;
      background: transparent;
    }
  }

  .zoom-image-close-btn {
    top: 16px;
    right: 16px;
  }

  .zoom-image-left-btn,
  .zoom-image-right-btn {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
  }

  .zoom-image-left-btn {
    left: 16px;
  }

  .zoom-image-right-btn {
    right: 16px;
  }

  .zoom-image-dots-wrapper {
    position: fixed;
    display: flex;
    gap: 8px;
    z-index: ${zIndex.MODAL + 1};
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
  }

  .zoom-image-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${colors.WHITE};
  }

  .zoom-image-dot-active {
    background-color: ${colors.BLACK};
  }

  #image-mobile-wrapper {
    width: 100vw;
    height: 100%;
    position: fixed;
    z-index: ${zIndex.MODAL};
    top: 0;
    left: 0;
    overflow: auto;

    @media (min-width: ${breakpoints.S}px) {
      width: 100%;
      height: 100vh;
    }
  }

  .zoom-image {
    display: block;
    aspect-ratio: 3 / 4;
    object-fit: contain;
    width: 220%;
    height: auto;

    @media (min-width: ${breakpoints.S}px) {
      width: auto;
    }
  }

  .blur-up {
    -webkit-filter: blur(5px);
    filter: blur(5px);
    transition:
      filter 200ms,
      -webkit-filter 200ms;
  }

  .blur-up.lazyloaded {
    -webkit-filter: blur(0);
    filter: blur(0);
  }
`;

type Props = {
  images: ProductImageListMobile;
  activeIndex: number;
  goToIndex: (index: number) => void;
  onClose: () => void;
};

export const ZoomImage = ({ images, activeIndex, goToIndex, onClose }: Props) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useBodyScrollLock({ isActive: true, excludeId: 'image-mobile-wrapper' });

  const { webp, jpg } = images[activeIndex].default;
  const { webp: zoomWebp, jpg: zoomJpg, alt: zoomAlt } = images[activeIndex].zoomed;

  const onClickRight = () => {
    if (activeIndex < images.length - 1) {
      goToIndex(activeIndex + 1);
    }
  };

  const onClickLeft = () => {
    if (activeIndex !== 0) {
      goToIndex(activeIndex - 1);
    }
  };

  const customClose = () => {
    const metaViewport = document.querySelector('meta[name="viewport"]');

    if (metaViewport) {
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
      );

      timeout.current = setTimeout(() => {
        metaViewport.setAttribute('content', 'width=device-width, initial-scale=1');
        onClose();
      }, 100);
    }
  };

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const adjustScrollPosition = () => {
      if (imageRef.current && containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const imageWidth = imageRef.current.clientWidth;
        const scrollLeft = (imageWidth - containerWidth) / 2;
        containerRef.current.scrollLeft = Math.max(0, scrollLeft);
        containerRef.current.scrollTop = 0;
      }
    };

    const img = imageRef.current;
    if (img) {
      img.onload = () => {
        adjustScrollPosition();
      };
    }
  }, [activeIndex]);

  const content = (
    <StyledZoomImageContainer>
      <button
        data-testid="zoom-image-mobile-close-btn"
        type="button"
        onClick={customClose}
        className="zoom-image-close-btn"
      >
        <Icon name="close" color={colors.BLACK} />
      </button>
      {activeIndex > 0 && (
        <button
          data-testid={`zoom-image-carousel-go-to-previous-picture-index-${activeIndex - 1}`}
          type="button"
          onClick={onClickLeft}
          className="zoom-image-left-btn"
        >
          <Icon name="arrowLeft" color={colors.BLACK} />
        </button>
      )}
      {activeIndex < images.length - 1 && (
        <button
          data-testid={`zoom-image-carousel-go-to-next-picture-index-${activeIndex + 1}`}
          type="button"
          onClick={onClickRight}
          className="zoom-image-right-btn"
        >
          <Icon name="arrowRight" color={colors.BLACK} />
        </button>
      )}
      {images.length > 1 && (
        <div className="zoom-image-dots-wrapper">
          {images.map((image, index) => {
            return (
              <div
                key={image.default.jpg}
                className={`zoom-image-dot ${activeIndex === index ? 'zoom-image-dot-active' : ''}`}
              />
            );
          })}
        </div>
      )}
      <button type="button" onClick={customClose} data-testid="image-zoom-close-btn-wrapper">
        <div id="image-mobile-wrapper" ref={containerRef}>
          <picture>
            <source srcSet={webp} data-srcSet={zoomWebp} type="image/webp" />
            <img
              ref={imageRef}
              className="zoom-image blur-up lazyload"
              src={jpg}
              srcSet={`${zoomJpg} 2x`}
              data-srcSet={zoomJpg}
              alt={zoomAlt}
            />
          </picture>
        </div>
      </button>
    </StyledZoomImageContainer>
  );

  return ReactDOM.createPortal(content, document.body);
};
